import React, { useMemo, useState } from 'react';
import cn from 'clsx';
import styles from '../index.module.scss';
import ActiveReviewNotesList from './active-review-notes-list';
import { ActiveReviewNote, mapActiveReviewNote } from './shared';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { SearchBox } from '@components-clean/core/search-box';
import { useDebouncedValue } from '@hooks-clean/index';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { SubjectNotesApi } from '@apis/bff-api/subject-monitoring/subject-notes-api';

type Props = {
    studyRef: string;
    // PgId, not recordId
    reviewId: number;
};

const NotesSection = ({ studyRef, reviewId }: Props) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.summary.notes' });
    const { t: tCommon } = useTranslation('common');

    const [search, setSearch] = useState('');
    const trimmedSearch = search.trim();
    const debouncedTrimmedSearch = useDebouncedValue(trimmedSearch, 500);

    const { data, isLoading, error } = SubjectNotesApi.useGetAllNotesForReview(studyRef, reviewId);

    const notes = useMemo<ActiveReviewNote[] | null>(() => {
        if (data == null) {
            return null;
        }

        const mappedNotes = data
            .map(mapActiveReviewNote)
            // `mapActiveReviewNote` returns null if the row is invalid
            .filter((x) => x != null)
            // Sort by recordId desc (secondary), then by observationDate desc (primary)
            .toSorted((a, z) => z.recordId - a.recordId)
            .toSorted((a, z) => new Date(z.observationDate).getTime() - new Date(a.observationDate).getTime());

        // Filter notes based on search (<any string>.includes('') always returns true)
        const normalizedSearch = debouncedTrimmedSearch.toLocaleLowerCase();
        return mappedNotes.filter((x) => {
            if (x.subjectNumber.toLocaleLowerCase().includes(normalizedSearch)) {
                return true;
            }
            if (x.text.toLocaleLowerCase().includes(normalizedSearch)) {
                return true;
            }
            if (x.createdBy.toLocaleLowerCase().includes(normalizedSearch)) {
                return true;
            }
            if (x.updatedBy.toLocaleLowerCase().includes(normalizedSearch)) {
                return true;
            }
            const formattedObservationDate = stringDateWithoutTimeFromUtc(x.observationDate, 'DD-MMM-yyyy');
            return formattedObservationDate?.toLocaleLowerCase().includes(normalizedSearch) ?? false;
        });
    }, [data, debouncedTrimmedSearch]);

    const isError = error != null;

    return (
        <Card
            data-testid="notes-card"
            className={styles.gridCell}
            bodyClassName={cn(styles.gridCellInner, styles.notesSectionInner)}
            padding="none"
            title={
                <>
                    {t('title')} {notes != null && <span className={styles.count}>({notes.length})</span>}
                </>
            }
        >
            <div>
                <SearchBox
                    data-testid="notes-card-search"
                    fillMode="flat"
                    showSearchIcon
                    value={search}
                    onChange={(e) => setSearch(typeof e.value === 'string' ? e.value : '')}
                />
            </div>

            {isLoading || isError ? (
                <div>
                    {isError ? (
                        <InfoField type="Error" message={tCommon('errorMessage')} />
                    ) : isLoading ? (
                        <Loader />
                    ) : null}
                </div>
            ) : (
                notes != null && (
                    <div className={styles.notesList}>
                        {/* Reset list when search changes */}
                        <ActiveReviewNotesList key={debouncedTrimmedSearch} notes={notes} />
                    </div>
                )
            )}
        </Card>
    );
};

export default NotesSection;
